import { add, isAfter, parseISO } from 'date-fns';
import {
    arrayFromTickRange,
    duration,
    endOfUnit,
    findValueNodeIndex,
    makeTick,
    makeTickRange,
    toDateStr,
} from './ticks';
import {
    SnapshotBase,
    Tick,
    TickRange,
    TickUnit,
    ValueNode,
    ValueSeries,
    ValueSeriesGenerator,
} from './types';

export function makeValueSeriesGenerator<
    S extends SnapshotBase,
    VN extends ValueNode,
>({
    snapshots,
    currentDate,
    generatePastValueNode,
    generateCurrentValueNode,
    generateFutureValueNode,
}: {
    snapshots: S[];
    currentDate: string;
    generatePastValueNode: (params: { snapshot?: S; tick: Tick }) => VN;
    generateCurrentValueNode: (params: { snapshot?: S; tick: Tick }) => VN;
    generateFutureValueNode: (params: { tick: Tick }) => VN;
}): ValueSeriesGenerator<VN> {
    return (range: TickRange) => {
        const values: VN[] = [];
        const nowT = makeTick(currentDate, range.unit);

        const includePastValues = range.startDate <= nowT.date;
        const includeFutureValues = range.endDate > nowT.date;

        if (includePastValues) {
            const snapshotValueSeries = valueSeriesFromSnapshots({
                snapshots,
                valueNodeFromSnapshot: (snapshot: S, tick: Tick) => {
                    return tick.date === nowT.date
                        ? generateCurrentValueNode({ snapshot, tick })
                        : generatePastValueNode({ snapshot, tick });
                },
                endDate: nowT.date,
                unit: range.unit,
            }) || {
                // If no snapshots exist, simulate a single-value series for
                // the current tick as null.
                ...makeTickRange(nowT.date, nowT.date, nowT.unit),
                startDateObj: nowT.dateObj,
                values: [generateCurrentValueNode({ tick: nowT })],
            };

            if (snapshotValueSeries.startDate > range.startDate) {
                // If snapshot value series starts *after* range.startDate,
                // fill preceding ticks with null values.

                const endDate = toDateStr(
                    add(
                        parseISO(snapshotValueSeries.startDate),
                        duration(-1, range.unit)
                    )
                );

                arrayFromTickRange({
                    startDate: range.startDate,
                    endDate,
                    unit: range.unit,
                }).forEach((tick) => {
                    const vn =
                        tick.date === nowT.date
                            ? generateCurrentValueNode({ tick })
                            : generatePastValueNode({ tick });

                    if (
                        tick.date >= range.startDate &&
                        tick.date <= range.endDate
                    ) {
                        values.push(vn);
                    }
                });
            }

            let snapshotRangeStartIndex = 0;
            let snapshotRangeEndIndex = undefined;

            // If snapshot value series starts *before* range.startDate,
            // only include values starting from range.startDate
            if (snapshotValueSeries.startDate < range.startDate) {
                const rangeStartTick = makeTick(range.startDate, range.unit);
                snapshotRangeStartIndex = findValueNodeIndex(
                    snapshotValueSeries,
                    rangeStartTick
                );
            }

            // If range.endDate is *before* the current date & *equal to or after* snapshots.startDate,
            // only include values up to the range.endDate tick.
            if (
                range.endDate < nowT.date &&
                range.endDate >= snapshotValueSeries.startDate
            ) {
                const rangeEndTick = makeTick(range.endDate, range.unit);
                snapshotRangeEndIndex =
                    findValueNodeIndex(snapshotValueSeries, rangeEndTick) + 1;
            }

            const relevantSnapshots = snapshotValueSeries.values.slice(
                snapshotRangeStartIndex,
                snapshotRangeEndIndex
            );

            for (const snapshot of relevantSnapshots) {
                if (
                    snapshot.date >= range.startDate &&
                    snapshot.date <= range.endDate
                ) {
                    values.push(snapshot);
                }
            }
        }

        if (includeFutureValues) {
            // Clamp "future start" to max of range start or current date
            const startDate =
                range.startDate > nowT.date
                    ? range.startDate
                    : toDateStr(add(nowT.dateObj, duration(1, range.unit)));

            const futureTicks = arrayFromTickRange({ ...range, startDate });

            futureTicks.forEach((tick) => {
                const vn = generateFutureValueNode({ tick });
                values.push(vn);
            });
        }

        return {
            ...range,
            startDateObj: parseISO(range.startDate),
            values,
        };
    };
}

function valueSeriesFromSnapshots<
    S extends SnapshotBase,
    VN extends ValueNode,
>({
    snapshots,
    valueNodeFromSnapshot,
    unit,
    endDate,
}: {
    snapshots: S[];
    valueNodeFromSnapshot: (snapshot: S, tick: Tick) => VN;
    unit: TickUnit;
    endDate: string;
}): ValueSeries<VN> | undefined {
    // Get the earliest snapshot (snapshots are in descending order)
    const earliestSnapshot = snapshots.at(-1);
    if (!earliestSnapshot) return;

    // Create a tick range from the earliest snapshot to the specified end date
    const startDate = makeTick(earliestSnapshot.dateTime, unit).date;
    const tickRange = makeTickRange(startDate, endDate, unit);
    const ticks = arrayFromTickRange(tickRange);

    // Initialize the values array with the same length as ticks
    const values: VN[] = new Array(ticks.length);

    // Start with the most recent snapshot
    let snapshotCursor = snapshots.length - 1;

    for (let i = 0; i < ticks.length; i++) {
        const tick = ticks[i];
        const tickPeriodEnd = endOfUnit(tick.dateObj, tick.unit);

        // Move the snapshot cursor back until we find the most recent snapshot
        // that is not after the end of the current tick period
        while (
            snapshotCursor > 0 &&
            !isAfter(snapshots[snapshotCursor - 1].dateTime, tickPeriodEnd)
        ) {
            snapshotCursor--;
        }

        // Create a value node for this tick using the appropriate snapshot
        values[i] = valueNodeFromSnapshot(snapshots[snapshotCursor], tick);
    }

    return {
        startDate,
        endDate,
        unit,
        startDateObj: parseISO(startDate),
        values,
    };
}
